import { useState } from 'react';
import { toast } from '@theonefamily/ui';
import { useApi } from '../hooks/useApi.js';
import { post } from '../lib/api.js';

interface LookalikeRegistration {
  id: string;
  original_domain: string;
  lookalike_domain: string;
  technique: string;
  risk_level: 'critical' | 'high' | 'medium';
  registered_at?: string;
  registrar?: string;
  registrant_country?: string;
  resolves: boolean;
  has_mx_record: boolean;
  has_ssl: boolean;
  ip_address?: string;
  status: 'new' | 'investigating' | 'confirmed_threat' | 'false_positive' | 'takedown_requested';
  first_detected_at: string;
  last_checked_at: string;
  notes?: string;
}

interface SummaryData {
  domains_monitored: number;
  new_this_week: number;
  active_threats: number;
  confirmed_phishing: number;
  total_registrations: number;
  confirmed_threats: number;
}

interface ListResponse {
  data: LookalikeRegistration[];
  hasMore: boolean;
}

interface SummaryResponse {
  data: SummaryData;
}

const TECHNIQUE_LABELS: Record<string, string> = {
  char_substitution: 'Char Substitution',
  char_omission: 'Char Omission',
  char_addition: 'Char Addition',
  tld_variation: 'TLD Variation',
  homoglyph: 'Homoglyph',
  hyphen_insertion: 'Hyphen Insert',
  keyword_prepend: 'Keyword Prepend',
  keyword_append: 'Keyword Append',
};

const STATUS_OPTIONS = [
  { value: 'new', label: 'New' },
  { value: 'investigating', label: 'Investigating' },
  { value: 'confirmed_threat', label: 'Confirmed Threat' },
  { value: 'false_positive', label: 'False Positive' },
  { value: 'takedown_requested', label: 'Takedown Requested' },
];

export function LookalikeRegistrationsPage() {
  const [filterRisk, setFilterRisk] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [statusUpdate, setStatusUpdate] = useState('');
  const [notesUpdate, setNotesUpdate] = useState('');
  const [scanLoading, setScanLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const params = new URLSearchParams();
  if (filterRisk) params.set('risk_level', filterRisk);
  if (filterStatus) params.set('status', filterStatus);
  const queryStr = params.toString();

  const { data: listData, loading, error, refresh } = useApi<ListResponse>(
    `/protect/lookalike-registrations${queryStr ? `?${queryStr}` : ''}`,
  );
  const { data: summaryData, refresh: refreshSummary } = useApi<SummaryResponse>(
    '/protect/lookalike-registrations?action=summary',
  );

  const registrations = listData?.data ?? [];
  const summary = summaryData?.data;
  const selectedReg = registrations.find(r => r.id === selectedId) ?? null;

  async function handleScanNow() {
    setScanLoading(true);
    try {
      await post('/protect/lookalike-registrations?action=scan_now');
      refresh();
      refreshSummary();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Scan failed');
    } finally {
      setScanLoading(false);
    }
  }

  async function handleUpdateStatus() {
    if (!selectedId || !statusUpdate) return;
    setActionLoading(true);
    try {
      await post(`/protect/lookalike-registrations/${selectedId}?action=update_status`, {
        status: statusUpdate,
        notes: notesUpdate || undefined,
      });
      setSelectedId(null);
      setStatusUpdate('');
      setNotesUpdate('');
      refresh();
      refreshSummary();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Update failed');
    } finally {
      setActionLoading(false);
    }
  }

  function riskBadge(risk: string) {
    const colors =
      risk === 'critical' ? 'bg-red-100 text-red-700' :
      risk === 'high' ? 'bg-orange-100 text-orange-700' :
      'bg-yellow-100 text-yellow-700';
    return (
      <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${colors}`}>
        {risk}
      </span>
    );
  }

  function techniqueBadge(technique: string) {
    return (
      <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-700">
        {TECHNIQUE_LABELS[technique] ?? technique}
      </span>
    );
  }

  function statusBadge(status: string) {
    const colors =
      status === 'new' ? 'bg-red-100 text-red-700' :
      status === 'investigating' ? 'bg-yellow-100 text-yellow-700' :
      status === 'confirmed_threat' ? 'bg-red-200 text-red-900 font-bold' :
      status === 'false_positive' ? 'bg-gray-100 text-gray-500' :
      status === 'takedown_requested' ? 'bg-purple-100 text-purple-700' :
      'bg-gray-100 text-gray-600';
    return (
      <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${colors}`}>
        {status.replace(/_/g, ' ')}
      </span>
    );
  }

  function rowBorderColor(risk: string) {
    if (risk === 'critical') return 'border-l-4 border-l-red-500';
    if (risk === 'high') return 'border-l-4 border-l-orange-400';
    return 'border-l-4 border-l-yellow-300';
  }

  const confirmedThreatRegs = registrations.filter(r => r.status === 'confirmed_threat');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading lookalike domain monitor...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-700">{error}</p>
        <button onClick={refresh} className="mt-2 text-sm text-red-600 underline">Retry</button>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Lookalike Domain Monitor</h2>
          <p className="text-gray-600 mt-1">
            Proactively detects newly registered domains that impersonate your monitored domains.
          </p>
        </div>
        <button
          onClick={handleScanNow}
          disabled={scanLoading}
          className="px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-md hover:bg-emerald-700 disabled:opacity-50"
        >
          {scanLoading ? 'Scanning...' : 'Scan Now'}
        </button>
      </div>

      {/* Summary Stats */}
      {summary && (
        <div className="grid grid-cols-4 gap-4 my-6">
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <p className="text-sm text-gray-500">Domains Monitored</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{summary.domains_monitored}</p>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <p className="text-sm text-gray-500">New This Week</p>
            <div className="flex items-center gap-2 mt-1">
              <p className="text-2xl font-bold text-gray-900">{summary.new_this_week}</p>
              {summary.new_this_week > 0 && (
                <span className="px-2 py-0.5 text-xs font-bold rounded-full bg-orange-100 text-orange-700">NEW</span>
              )}
            </div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <p className="text-sm text-gray-500">Active Threats</p>
            <p className={`text-2xl font-bold mt-1 ${summary.active_threats > 0 ? 'text-orange-600' : 'text-gray-900'}`}>
              {summary.active_threats}
            </p>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <p className="text-sm text-gray-500">Confirmed Phishing Domains</p>
            <div className="flex items-center gap-2 mt-1">
              <p className={`text-2xl font-bold ${summary.confirmed_phishing > 0 ? 'text-red-600' : 'text-gray-900'}`}>
                {summary.confirmed_phishing}
              </p>
              {summary.confirmed_phishing > 0 && (
                <span className="px-2 py-0.5 text-xs font-bold rounded-full bg-red-100 text-red-700">CRITICAL</span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Alert Banner for confirmed threats */}
      {confirmedThreatRegs.length > 0 && (
        <div className="mb-6 bg-red-50 border border-red-300 rounded-lg p-4 flex items-start gap-3">
          <div className="text-red-600 text-lg font-bold mt-0.5">!</div>
          <div>
            <p className="font-semibold text-red-800">
              {confirmedThreatRegs.length} confirmed phishing domain{confirmedThreatRegs.length > 1 ? 's' : ''} detected
            </p>
            <p className="text-sm text-red-700 mt-1">
              {confirmedThreatRegs.map(r => r.lookalike_domain).join(', ')}
            </p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-3 mb-4">
        <select
          value={filterRisk}
          onChange={(e) => setFilterRisk(e.target.value)}
          className="border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-700"
        >
          <option value="">All Risk Levels</option>
          <option value="critical">Critical</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
        </select>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-700"
        >
          <option value="">All Statuses</option>
          {STATUS_OPTIONS.map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        {(filterRisk || filterStatus) && (
          <button
            onClick={() => { setFilterRisk(''); setFilterStatus(''); }}
            className="px-3 py-2 text-sm text-gray-500 hover:text-gray-700 underline"
          >
            Clear filters
          </button>
        )}
      </div>

      {/* Main Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Lookalike Domain</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Original Domain</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Technique</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Risk</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">DNS Status</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">First Detected</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Status</th>
              <th className="text-right px-4 py-3 font-medium text-gray-600">Actions</th>
            </tr>
          </thead>
          <tbody>
            {registrations.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-4 py-10 text-center text-gray-500">
                  No lookalike domains detected. Run a scan or wait for the daily scan at 6am UTC.
                </td>
              </tr>
            ) : registrations.map((reg) => (
              <tr
                key={reg.id}
                className={`border-b border-gray-100 hover:bg-gray-50 ${rowBorderColor(reg.risk_level)}`}
              >
                <td className="px-4 py-3 font-mono font-medium text-gray-900">
                  {reg.lookalike_domain}
                </td>
                <td className="px-4 py-3 text-gray-600 font-mono">
                  {reg.original_domain}
                </td>
                <td className="px-4 py-3">
                  {techniqueBadge(reg.technique)}
                </td>
                <td className="px-4 py-3">
                  {riskBadge(reg.risk_level)}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-xs font-medium ${reg.resolves ? 'text-green-600' : 'text-gray-400'}`}>
                      {reg.resolves ? '✓ Resolves' : '✗ No DNS'}
                    </span>
                    {reg.has_mx_record && (
                      <span className="text-xs font-medium text-orange-600">✉ MX</span>
                    )}
                    {reg.has_ssl && (
                      <span className="text-xs font-medium text-gray-500">SSL</span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3 text-gray-500 text-xs">
                  {new Date(reg.first_detected_at).toLocaleDateString()}
                </td>
                <td className="px-4 py-3">
                  {statusBadge(reg.status)}
                </td>
                <td className="px-4 py-3 text-right">
                  <button
                    onClick={() => {
                      setSelectedId(reg.id);
                      setStatusUpdate(reg.status);
                      setNotesUpdate(reg.notes ?? '');
                    }}
                    className="text-xs text-emerald-600 hover:text-emerald-800"
                  >
                    Details
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Detail Panel */}
      {selectedReg && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-end">
          <div className="w-[480px] h-full bg-white shadow-xl flex flex-col overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Registration Detail</h3>
              <button
                onClick={() => setSelectedId(null)}
                className="text-gray-400 hover:text-gray-600 text-xl leading-none"
              >
                &times;
              </button>
            </div>

            <div className="p-6 flex-1 space-y-5">
              {/* Lookalike + Original */}
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Lookalike Domain</p>
                <p className="text-lg font-mono font-bold text-gray-900">{selectedReg.lookalike_domain}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Original Domain</p>
                <p className="font-mono text-gray-700">{selectedReg.original_domain}</p>
              </div>

              {/* Risk + Technique */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Risk Level</p>
                  {riskBadge(selectedReg.risk_level)}
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Technique</p>
                  {techniqueBadge(selectedReg.technique)}
                </div>
              </div>

              {/* DNS Status */}
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">DNS Status</p>
                <div className="flex items-center gap-4">
                  <div className={`flex items-center gap-1 text-sm font-medium ${selectedReg.resolves ? 'text-green-600' : 'text-gray-400'}`}>
                    <span>{selectedReg.resolves ? '✓' : '✗'}</span>
                    <span>Resolves</span>
                  </div>
                  <div className={`flex items-center gap-1 text-sm font-medium ${selectedReg.has_mx_record ? 'text-orange-600' : 'text-gray-400'}`}>
                    <span>{selectedReg.has_mx_record ? '✓' : '✗'}</span>
                    <span>Has MX</span>
                  </div>
                  <div className={`flex items-center gap-1 text-sm font-medium ${selectedReg.has_ssl ? 'text-blue-600' : 'text-gray-400'}`}>
                    <span>{selectedReg.has_ssl ? '✓' : '✗'}</span>
                    <span>SSL</span>
                  </div>
                </div>
                {selectedReg.ip_address && (
                  <p className="text-sm text-gray-600 mt-2">IP: <span className="font-mono">{selectedReg.ip_address}</span></p>
                )}
              </div>

              {/* WHOIS data if available */}
              {(selectedReg.registered_at || selectedReg.registrar || selectedReg.registrant_country) && (
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">WHOIS</p>
                  <div className="space-y-1 text-sm text-gray-700">
                    {selectedReg.registered_at && (
                      <p>Registered: {new Date(selectedReg.registered_at).toLocaleDateString()}</p>
                    )}
                    {selectedReg.registrar && <p>Registrar: {selectedReg.registrar}</p>}
                    {selectedReg.registrant_country && <p>Country: {selectedReg.registrant_country}</p>}
                  </div>
                </div>
              )}

              {/* Dates */}
              <div className="grid grid-cols-2 gap-4 text-sm text-gray-600">
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">First Detected</p>
                  <p>{new Date(selectedReg.first_detected_at).toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Last Checked</p>
                  <p>{new Date(selectedReg.last_checked_at).toLocaleString()}</p>
                </div>
              </div>

              {/* Status Update */}
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Update Status</p>
                <select
                  value={statusUpdate}
                  onChange={(e) => setStatusUpdate(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm mb-2"
                >
                  {STATUS_OPTIONS.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
                <textarea
                  value={notesUpdate}
                  onChange={(e) => setNotesUpdate(e.target.value)}
                  placeholder="Investigation notes (optional)"
                  rows={3}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm resize-none"
                />
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex gap-3">
              <button
                onClick={handleUpdateStatus}
                disabled={actionLoading || !statusUpdate}
                className="flex-1 px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-md hover:bg-emerald-700 disabled:opacity-50"
              >
                {actionLoading ? 'Saving...' : 'Save Status'}
              </button>
              <button
                onClick={() => setSelectedId(null)}
                className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-200"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
